import { FormHelperText, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';

import { CoreFormControl } from '@youscience/core';

import { Ratings as RatingsEnum, SchoolReportRequest, SchoolReportSchema } from '@youscience/commonapp-service-common';

import SectionAccordion from '@components/SectionAccordion';

import { FormState } from '@constants/commonApp/types';

import ContinueButton from '@components/ContinueButton';
import { StyledFieldsContainer } from '@pages/RecommenderSpace/components/Card.styles';
import { useApplicantFormSection } from '@pages/StudentDetailsPage/hooks/useApplicantFormSection';

export type RatingsFields = NonNullable<Partial<SchoolReportRequest['ratings']>>;

interface RatingsProps {
  ratings?: RatingsFields | null;
  onFormChange: (formState: FormState<RatingsFields>) => void;
  onExpand: () => void;
  onContinue: () => void;
  isExtended: boolean;
  isSaved: boolean;
}

const validator = SchoolReportSchema.shape.ratings;

export const Ratings = ({ ratings = {}, onFormChange, onExpand, onContinue, isExtended, isSaved }: RatingsProps) => {
  const [formState, setFormStateWithErrors] = useApplicantFormSection<RatingsFields, typeof validator>(
    ratings as RatingsFields,
    validator,
    isSaved,
    onFormChange,
  );

  const isShowErrors = new URLSearchParams(window.location.search).get('showErrors') === 'true';

  const handleSelectChange = (field: string) => (event: SelectChangeEvent<unknown>) => {
    setFormStateWithErrors({ [field]: (event.target.value as string) || '' });
  };

  return (
    <SectionAccordion
      title="Ratings"
      isExpanded={isExtended}
      onChange={onExpand}
      isChanged={formState.hasChanged}
      showCheckmark={formState.isSaved && !!formState.isFormValid}
      isError={isShowErrors && !formState.isFormValid}
    >
      <StyledFieldsContainer>
        <CoreFormControl fullWidth error={!!formState.errors.academics}>
          <InputLabel required>
            Compared to other students in his or her class, how do you rate this applicant in terms of academic
            achievements.
          </InputLabel>

          <Select
            label="Compared to other students in his or her class, how do you rate this applicant in terms of academic achievements."
            value={formState.data?.academics ?? ''}
            onChange={handleSelectChange('academics')}
          >
            {Object.entries(RatingsEnum).map(([key, value]) => {
              return (
                <MenuItem key={value} value={value}>
                  {key}
                </MenuItem>
              );
            })}
          </Select>

          {formState.errors.academics && <FormHelperText>{formState.errors.academics}</FormHelperText>}
        </CoreFormControl>

        <CoreFormControl fullWidth error={!!formState.errors.extracurricular}>
          <InputLabel required>
            Compared to other students in his or her class, how do you rate this applicant in terms of extracurricular
            acomplishments.
          </InputLabel>

          <Select
            label="Compared to other students in his or her class, how do you rate this applicant in terms of extracurricular accomplishments."
            value={formState.data?.extracurricular ?? ''}
            onChange={handleSelectChange('extracurricular')}
          >
            {Object.entries(RatingsEnum).map(([key, value]) => {
              return (
                <MenuItem key={value} value={value}>
                  {key}
                </MenuItem>
              );
            })}
          </Select>
          {formState.errors.extracurricular && <FormHelperText>{formState.errors.extracurricular}</FormHelperText>}
        </CoreFormControl>

        <CoreFormControl fullWidth error={!!formState.errors.personal}>
          <InputLabel required>
            Compared to other students in his or her class, how do you rate this applicant in terms of personal
            qualities and character.
          </InputLabel>

          <Select
            label="Compared to other students in his or her class, how do you rate this applicant in terms of personal qualities and character."
            value={formState.data?.personal ?? ''}
            onChange={handleSelectChange('personal')}
          >
            {Object.entries(RatingsEnum).map(([key, value]) => {
              return (
                <MenuItem key={value} value={value}>
                  {key}
                </MenuItem>
              );
            })}
          </Select>

          {formState.errors.personal && <FormHelperText>{formState.errors.personal}</FormHelperText>}
        </CoreFormControl>

        <CoreFormControl fullWidth error={!!formState.errors.overall}>
          <InputLabel required>
            Compared to other students in his or her class, how do you rate this applicant overall.
          </InputLabel>

          <Select
            label="Compared to other students in his or her class, how do you rate this applicant overall."
            value={formState.data?.overall ?? ''}
            onChange={handleSelectChange('overall')}
          >
            {Object.entries(RatingsEnum).map(([key, value]) => {
              return (
                <MenuItem key={value} value={value}>
                  {key}
                </MenuItem>
              );
            })}
          </Select>
          {formState.errors.overall && <FormHelperText>{formState.errors.overall}</FormHelperText>}
        </CoreFormControl>
        <ContinueButton onContinue={onContinue} />
      </StyledFieldsContainer>
    </SectionAccordion>
  );
};
